import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './index.css';
import { Button, message } from 'antd';
import Account from './Account';
import Inventory from './Inventory';
import { getCustomerAddress, getExtraWork, packageCustomerData, getStorageOrdersByEmail, getCustomerByEmail } from '../../../data/API';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useInventory from '../../../hooks/useInventory';
import { resetStore } from '../store/actionCreators';
import { useDispatch } from 'react-redux';

const Dashboard = (props) => {
  const [searchParams] = useSearchParams();
  const tabId = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState('account');
  const [customerData, setCustomerData] = useState(null);
  const [extraWork, setExtraWork] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasStorageOrders, setHasStorageOrders] = useState(false);
  const [userType, setUserType] = useState(null);
  const navigate = useNavigate();
  const { inventoryList, refreshInventory } = useInventory();
  const dispatch = useDispatch();

  const getLoginType = () => {
    return localStorage.getItem('loginType') || "Storage On-Demand";
  };

  const getUserTypeFromToken = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found in getUserTypeFromToken');
        return 'customer';
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('Token payload:', payload);
      return payload.type || 'customer';
    } catch (error) {
      console.error('Error decoding token in getUserTypeFromToken:', error);
      return 'customer';
    }
  };

  const loginType = getLoginType();

  // Smart detection: Use token type to determine data fetching strategy
  const isStorageOnDemand = loginType === "Storage On-Demand" && userType === 'customer';
  const hasBothOrderTypes = userType === 'both_customer';
  const isRentalCustomer = userType === 'rental_customer' || userType === 'both_customer';

  // Enhanced condition: Show inventory if user has storage orders regardless of token type
  const canAccessInventory = isStorageOnDemand || hasBothOrderTypes || hasStorageOrders;

  useEffect(() => {
    if(tabId) {
      if (!userType) {
        return;
      }

      if(tabId === "inventory" && !canAccessInventory) {
        setActiveTab('account');
        navigate(window.location.pathname + '?tab=account', { replace: true });
      } else {
        if(tabId === "account") {
          dispatch(resetStore())
        }
        setActiveTab(tabId)
      }
    }
  }, [tabId, canAccessInventory, userType]);

  useEffect(() => {
    const currentUserType = getUserTypeFromToken();
    console.log('Dashboard mounted - setting userType:', currentUserType);
    setUserType(currentUserType);

    props.setShowHideHeader(true);
    props.setshowHideFooter(false);
    props.setshowHideinnerFooter(false);
    getExtarworkInfo();

    // dispatch(resetStore())
  }, []);

  // Call getUserData after userType is set
  useEffect(() => {
    if (userType) {
      console.log('UserType is set, calling getUserData');
      getUserData();
    }
  }, [userType]);

  // Check for storage orders when customerData is available
  useEffect(() => {
    if (customerData && !isStorageOnDemand && !hasBothOrderTypes) {
      checkForStorageOrders();
    }
  }, [customerData]);

  const updateURL = (value) => {
    if (value === 'inventory' && !canAccessInventory) {
      value = 'account';
    }
    
    setActiveTab(value);
    const currentURL = window.location.pathname;
    const separator = currentURL.includes('?') ? '&' : '?';

    const updatedURL = currentURL + separator + 'tab=' + value;
    navigate(updatedURL);
  };

  const getUserData = async (forceRefresh = false) => {
    if (forceRefresh) setIsRefreshing(true);
    
    try {
      let response;
      const currentTokenType = getUserTypeFromToken();
      console.log('getUserData called with tokenType:', currentTokenType);

      if (currentTokenType === 'customer') {
        response = await getCustomerAddress();

        if (response?.data?.success) {
          setCustomerData(response.data.data);
        }
      }
      else if (currentTokenType === 'rental_customer') {
        try {
          response = await packageCustomerData();
          
          if (response?.data) {
            const normalizedData = normalizeCustomerData(response.data);
            setCustomerData(normalizedData);
          }
        } catch (apiError) {
          if (apiError.response?.status === 404) {
            console.warn("packageCustomerData returned 404 for rental_customer");
            const authData = JSON.parse(localStorage.getItem('authData') || '{}');
            setCustomerData({
              id: authData.id,
              name: authData.name,
              email: authData.email,
              custormerData: {
                mobile: authData.mobile,
                secondary_mobile: authData.secondary_mobile
              }
            });
          } else {
            throw apiError;
          }
        }
      }
      else if (currentTokenType === 'both_customer') {
        try {
          response = await packageCustomerData();
          
          if (response?.data) {
            const normalizedData = normalizeCustomerData(response.data);
            setCustomerData(normalizedData);
            console.log('Loaded both_customer data via Django API:', normalizedData);
          }
        } catch (apiError) {
          console.error('Error fetching both_customer data via packageCustomerData:', apiError);
          const token = localStorage.getItem('token');
          const authData = JSON.parse(localStorage.getItem('authData') || '{}');
          
          setCustomerData({
            id: authData.id,
            name: authData.name,
            email: authData.email,
            lastLoggedIn: new Date().toISOString(),
            token: token,
            custormerData: {
              mobile: authData.mobile,
              secondary_mobile: authData.secondary_mobile,
              delivery_address: null,
              pickup_address: null
            }
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      if (error.response?.status !== 404) {
        message.error("Failed to fetch your account information.");
      }
    } finally {
      if (forceRefresh) setIsRefreshing(false);
    }
  };

  const handleOrderUpdate = async () => {
    const currentType = getUserTypeFromToken();
    if (currentType === 'rental_customer' || currentType === 'both_customer') {
      await getUserData(true);
    }
  };

  // Check if user has storage orders regardless of token type
  const checkForStorageOrders = async () => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userEmail = customerData?.email || authData.email;
      
      if (!userEmail) {
        console.log('No email found for storage order check');
        return;
      }
      
      console.log('Checking storage orders for email:', userEmail);
      
      // First check if user exists in storage system
      const customerCheckResponse = await getCustomerByEmail(userEmail);
      if (customerCheckResponse?.data?.success && customerCheckResponse?.data?.data) {
        // Then check for storage orders
        const storageResponse = await getStorageOrdersByEmail(userEmail);
        if (storageResponse?.data?.success && storageResponse.data.data.orders?.length > 0) {
          console.log('Found storage orders, enabling inventory access');
          setHasStorageOrders(true);
        }
      }
    } catch (error) {
      console.log('No storage orders found or error checking:', error.message);
      setHasStorageOrders(false);
    }
  };

  const normalizeCustomerData = (data) => {
    if (data.custormerData && !data.data) {
      return data;
    }
    
    if (data.data && data.data.custormerData) {
      return {
        id: data.data.id,
        name: data.data.first_name ? `${data.data.first_name} ${data.data.last_name}` : data.data.name,
        email: data.data.email,
        lastLoggedIn: data.data.lastLoggedIn,
        phone_number: data.data.phone_number,
        secondary_phone_number: data.data.secondary_phone_number,
        company_name: data.data.company_name,
        token: data.data.token,
        refreshToken: data.data.refreshToken,
        custormerData: data.data.custormerData
      };
    }
    
    return data;
  };

  const getExtarworkInfo = async () => {
    try {
      let response = await getExtraWork();
      if (response?.data) {
        setExtraWork(response?.data.results)
      }
    } catch (err) {
      if (isStorageOnDemand) {
        return message.error('error fetching extra work info windows', err);
      }
      console.log('Error fetching extra work info, but not showing error in Rent Moving Boxes mode');
    }
  };

  return (
    <Container fluid={true} className="p-0">
      <Row className="align-items-center justify-content-center top-bar">
        <Col
          xxl={isStorageOnDemand ? 3 : 6}
          xl={isStorageOnDemand ? 3 : 6}
          lg={isStorageOnDemand ? 4 : 8}
          md={6}
          sm={6}
          xs={6}
          className={`h-100 ${
            activeTab === 'account' ? 'bar-active' : ''
          } p-0 text-center tab-item d-flex justify-content-center align-items-center cursor-pointer`}
          onClick={() => updateURL('account')}>
          <p className="mb-0 text-white tab-title">
            {' '}
            <i className="fa fa-user-circle mr-2"></i> Account
          </p>
        </Col>
        {canAccessInventory && (
          <Col
            xxl={3}
            xl={3}
            lg={4}
            md={6}
            sm={6}
            xs={6}
            className={`h-100 ${
              activeTab === 'inventory' ? 'bar-active' : ''
            } p-0 text-center tab-item d-flex justify-content-center align-items-center cursor-pointer`}
            onClick={() => updateURL('inventory')}>
            <p className="mb-0 text-white tab-title">
              {' '}
              <i className="fas fa-sitemap mr-2"></i> Inventory
            </p>
          </Col>
        )}
      </Row>

      <div className='tab-content'>
        {activeTab === 'account' ? (
          <Account 
            customerData={customerData} 
            getUserData={getUserData}
            handleOrderUpdate={handleOrderUpdate}
            handleStorageAccountUpdate={() => { /* No-op for both_customer */ }} 
            isRefreshing={isRefreshing}
            extraWork={extraWork} 
          />
        ) : canAccessInventory ? (
          <Inventory inventoryList={inventoryList} onPhotoUploaded={refreshInventory} />
        ) : (
          <Account 
            customerData={customerData} 
            getUserData={getUserData}
            handleOrderUpdate={handleOrderUpdate}
            handleStorageAccountUpdate={() => { /* No-op for both_customer */ }} 
            isRefreshing={isRefreshing}
            extraWork={extraWork} 
          />
        )}
      </div>

      {(canAccessInventory && activeTab === 'inventory' && inventoryList && inventoryList.length > 0 && inventoryList.some(item => !item.delivery_order_id)) && (
      <div className='need-more py-4'>
        <Row className='justify-content-center align-items-center'>
          <Col xxl={4} xl={4} lg={5} md={6} sm={12} xs={12} className="text-center" >
            <p className='mb-0 pb-1 need-more-title fw-600 py-4'>Ready to store your items?</p>
            <Button type='primary' size='large' className='fw-600 more-btn' onClick={() => navigate("/storage-box/my-account/request-delivery")}>Request box pick up</Button>

            {/* <p className='fw-400 redirect-to pt-3 cursor-pointer' onClick={() => navigate("/packages")}>Or check out our moving boxes rental service</p> */}
          </Col>
        </Row>
      </div>
    )}
      
    </Container>
  );
};

export default Dashboard;

Dashboard.propTypes = {
  setShowHideHeader: PropTypes.func,
  setshowHideFooter: PropTypes.func,
  setshowHideinnerFooter: PropTypes.func
};
